"use client";

import { useMemo, useState } from "react";
import { DocumentPreview } from "@/components/tool-kit/document-preview";
import {
  Checkbox,
  Panel,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY } from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  buildGibbsReflection,
  GIBBS_STAGES,
  type GibbsInput,
  type GibbsStage,
} from "@/lib/tool-kit/essay-templates/gibbs";
import { countWords } from "@/lib/tool-kit/essay-templates/text";

/** English strings. A translated content file overrides these through `ui`. */
export const GIBBS_REFLECTIVE_CYCLE_GENERATOR_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  topicLabel: "What are you reflecting on?",
  topicPlaceholder: "e.g. Leading my group's first project meeting",
  headingsLabel: "Give each stage its own heading",
  stagesHeading: "The six stages",
  stageFieldLabel: "{n}. {stage} — {question}",
  wordCount: "one: word|other: words",
  fileName: "gibbs-reflection",
  document: {
    heading: "Your reflection",
    prompt: "To write",
    guidance: "Tip",
    copy: "Copy",
    copied: "Copied!",
    downloadText: "Download .txt",
    downloadMarkdown: "Download .md",
    print: "Print",
    remaining: "one: stage still to write|other: stages still to write",
    complete: "All six stages are written.",
  },
  defaultTitle: "Reflective Account",
  titleWithTopic: "Reflection: {topic}",
  subtitle: "Structured with Gibbs' Reflective Cycle (Gibbs, 1988)",
  voiceNote:
    "Write in the first person — “I noticed”, “I felt” — because the subject is your own experience. Keep the description short and give most of your words to evaluation and analysis.",
  stageHeading: "{n}. {stage}",
  stagePrompt: "{question} {guide}",
  stagePromptNamed: "{stage} — {question} {guide}",
  stageNames: {
    description: "Description",
    feelings: "Feelings",
    evaluation: "Evaluation",
    analysis: "Analysis",
    conclusion: "Conclusion",
    action: "Action plan",
  },
  stageQuestions: {
    description: "What happened?",
    feelings: "What went through your mind, and how did you feel?",
    evaluation: "What went well, and what went badly?",
    analysis: "Why did it go the way it did?",
    conclusion: "What have you learned?",
    action: "What will you do next time?",
  },
  stageGuides: {
    description:
      "Set the scene briefly: where and when it was, who was involved, what you did, and how it ended. Keep it factual — this is context, not analysis.",
    feelings:
      "Describe your thoughts and emotions before, during, and after, and how you think others felt. Be honest; you don't need to explain them yet.",
    evaluation:
      "Weigh the positives against the negatives, including what you and others contributed to each. Most experiences have both.",
    analysis:
      "Explain the reasons, and connect them to theory, research, or professional guidance — and cite it. This is usually the longest stage.",
    conclusion:
      "Sum up what the experience taught you and what else you could have done.",
    action:
      "Set specific steps — what you will do, how you will build the skill, and by when — so the plan can be checked later.",
  },
  referenceHeading: "Reference",
  referenceNote:
    "This is the APA 7 form of the reference. If you read a later reprint, or your course uses another style, cite the version and style you used.",
  reference: {
    before: "Gibbs, G. (1988). ",
    title: "Learning by doing: A guide to teaching and learning methods",
    after: ". Further Education Unit, Oxford Polytechnic.",
  },
  example: {
    topic: "My first patient handover on a clinical placement",
    description:
      "In the second week of my surgical placement, my supervising nurse asked me to hand over two of our patients to the night-shift nurse. I had prepared notes, but I began with each patient's full history and ran out of time before I reached the overnight plan. The night nurse had to ask several questions to find out which patient needed hourly observations, and my supervisor stepped in to finish the handover.",
    feelings:
      "Before the handover I felt prepared, because I had written everything down. Once I noticed the night nurse waiting for the key information, I became flustered and started reading faster instead of picking out the priorities. Afterwards I was disappointed in myself, but relieved that my supervisor had made sure nothing was missed.",
    evaluation:
      "On the positive side, my information was accurate and complete, and I was honest when I could not answer a question. What went badly was the order: the most urgent point, the hourly observations, came last. The handover took about twice as long as usual, and the night nurse could not easily pick out what mattered.",
    analysis:
      "I think it went wrong because I organized my notes as a story rather than by priority. Reading about structured handover tools afterwards, I found that SBAR — Situation, Background, Assessment, Recommendation — exists to put the current situation and the actions needed first. My supervisor's handovers follow that order, which is why they are short and easy to follow. I had also not practiced aloud, so under pressure I fell back on reading my notes word for word.",
    conclusion:
      "I have learned that a complete handover is not the same as a useful one: the person taking over needs the priorities first. I could have asked my supervisor to listen to a practice run, or used the ward's SBAR template to structure my notes.",
    action:
      "For my next three handovers I will write my notes in SBAR order and rehearse each one aloud beforehand. I will ask my supervisor for feedback after each one, and by the end of the placement I aim to hand over four patients without prompting.",
  },
} as const;

const EMPTY: GibbsInput = {
  topic: "",
  headings: true,
  description: "",
  feelings: "",
  evaluation: "",
  analysis: "",
  conclusion: "",
  action: "",
};

export default function GibbsReflectiveCycleGeneratorWidget() {
  const strings = useToolUi(GIBBS_REFLECTIVE_CYCLE_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [input, setInput] = useState<GibbsInput>(EMPTY);
  const doc = useMemo(
    () => buildGibbsReflection(input, strings),
    [input, strings]
  );
  const update = (key: GibbsStage | "topic") => (value: string) =>
    setInput((previous) => ({ ...previous, [key]: value }));

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() =>
            setInput((previous) => ({
              ...EMPTY,
              ...strings.example,
              headings: previous.headings,
            }))
          }
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setInput(EMPTY)}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      <Panel>
        <TextField
          id="gr-topic"
          label={strings.topicLabel}
          onChange={update("topic")}
          placeholder={strings.topicPlaceholder}
          value={input.topic}
        />
        <Checkbox
          checked={input.headings}
          label={strings.headingsLabel}
          onChange={(headings) =>
            setInput((previous) => ({ ...previous, headings }))
          }
        />
      </Panel>

      <Panel title={strings.stagesHeading}>
        {GIBBS_STAGES.map((stage, index) => {
          const words = countWords(input[stage]);
          return (
            <TextAreaField
              hint={
                <>
                  {strings.stageGuides[stage]}{" "}
                  <span className="whitespace-nowrap font-medium">
                    · {fmt(words)}
                    {tPlural(strings.wordCount, locale, words)}
                  </span>
                </>
              }
              id={`gr-${stage}`}
              key={stage}
              label={t(strings.stageFieldLabel, {
                n: index + 1,
                stage: strings.stageNames[stage],
                question: strings.stageQuestions[stage],
              })}
              onChange={update(stage)}
              prose
              rows={4}
              value={input[stage]}
            />
          );
        })}
      </Panel>

      <DocumentPreview
        doc={doc}
        fileName={strings.fileName}
        idPrefix="gr"
        labels={strings.document}
      />
    </div>
  );
}
